import { useState, useEffect } from 'react';
import { cn } from '@workspace/ui/lib/utils';
import { createTile, editTile } from '@/src/actions/tiles';
import { AddVideoIcon, Icon, useToast } from '@/src/components/ui';
import { useEditingHoverControl } from '../../../hooks/useEditingHoverControl';
import { useEditableTextarea } from '@/src/hooks/use-editable-textarea';
import { Textarea } from '@workspace/ui/components/textarea';
import { Button } from '@workspace/ui/components/button';
import { useClickOrDrag } from '../../../hooks/useClickOrDrag';
import {
  extractYouTubeId,
  getYouTubeThumbnail,
  getYouTubeVideoInfoNoAPI,
  isValidYouTubeUrl,
  YouTubeVideoInfo,
} from '@/src/utils/youtube';
import { IUITile } from '@/src/types/ITile';
import { YouTubeModal } from '../YoutubeModal';
import { getTileEditStatus } from '@/src/utils/helpers';
import { useTilesStore } from '../../../store/tiles-store';
import { LoadingOverlay } from '../LoadingOverlay';
import { useProfileUIStore } from '../../../store';

type Props = {
  isMyProfile?: boolean;
  onHoverStateChange?: (shouldDisable: boolean) => void;
  tile: IUITile;
  touchedTileId: string | null;
  setTouchedTileId: (id: string | null) => void;
  isMobileScreen?: boolean;
};

export const VideoTile = ({
  isMyProfile,
  onHoverStateChange,
  tile,
  touchedTileId,
  setTouchedTileId,
  isMobileScreen,
}: Props) => {
  const toast = useToast();

  const [videoInfo, setVideoInfo] = useState<Partial<YouTubeVideoInfo> | null>(
    null
  );
  const [videoError, setVideoError] = useState<string | null>(null);
  const [loadingVideoInfo, setLoadingVideoInfo] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const is2x2 = tile.w === 2 && tile.h === 6;
  const is1x2 = tile.w === 1 && tile.h === 6;
  const is2x1 = tile.w === 2 && tile.h === 3;
  const is1x1 = tile.w === 1 && tile.h === 3;

  const setTileLoading = useTilesStore((state) => state.setTileLoading);
  const previewMode = useProfileUIStore((state) => state.previewMode);
  const updateTile = useTilesStore((state) => state.updateTile);

  const { handleStartEditing, handleStopEditing } =
    useEditingHoverControl(onHoverStateChange);

  const { onMouseDown, onTouchStart } = useClickOrDrag(() => {
    handleStartEdit();
  });

  const {
    tempValue,
    value,
    isEditing,
    inputRef,
    handleChange,
    handleKeyDown,
    submitEdit,
    startEdit,
  } = useEditableTextarea({
    initialValue: tile.content || '',
    onSubmit: async (tempValue) => {
      const { hasContent } = getTileEditStatus(
        tile.content as string,
        tempValue
      );
      const isValidUrl = isValidYouTubeUrl(tempValue);
      setTileLoading(tile.i, true);
      try {
        if (tempValue && isValidUrl) {
          let response;
          await loadVideoInfo(tempValue);
          toast.updating('Adding video');

          const thumbnail = getYouTubeThumbnail(tempValue);

          if (!thumbnail) {
            toast.error('Failed to fetch video thumbnail');
            return;
          }
          if (!hasContent) {
            const { iconName, ...tileWithoutIconName } = tile;

            response = await createTile({
              ...tileWithoutIconName,
              content: tempValue,
              title: thumbnail,
            });
            if (!response) {
              toast.error('Failed to add video');
              return;
            }
          } else {
            response = await editTile(
              {
                content: tempValue,
                title: videoInfo?.title || 'YouTube Video',
              },
              tile.id as number
            );
            if (!response) {
              toast.error('Failed to update video');
              return;
            }
          }
          updateTile(tile.i, {
            ...tile,
            ...response,
            id: response.id,
            static: false,
          });
          toast.updated('Video added');
        } else if (tempValue) {
          setVideoError('Please attach YouTube video link');
          return;
        } else {
          updateTile(tile.i, {
            content: '',
            static: true,
          });
          setVideoInfo(null);
          setVideoError(null);
        }
      } catch (error) {
        toast.error('Failed to update video tile');
      } finally {
        setTileLoading(tile.i, false);
        updateTile(tile.i, { isEditing: false });
      }
      handleStopEditing();
    },
  });

  const loadVideoInfo = async (url: string) => {
    const videoId = extractYouTubeId(url);
    if (!videoId) {
      setVideoError('Please attach YouTube video link');
      return;
    }
    setLoadingVideoInfo(true);
    setVideoError(null);
    try {
      const info = await getYouTubeVideoInfoNoAPI(videoId);
      if (info) {
        setVideoInfo(info);
      } else {
        setVideoError('Could not load video information');
      }
    } catch (error) {
      setVideoError('Failed to load video information');
      console.error('Error loading video info:', error);
    } finally {
      setLoadingVideoInfo(false);
    }
  };

  useEffect(() => {
    if (tile.content && isValidYouTubeUrl(tile.content) && !videoInfo) {
      loadVideoInfo(tile.content);
    }
  }, [tile.content]);

  const handleStartEdit = () => {
    if (previewMode) return;
    updateTile(tile.i, { isEditing: true });
    startEdit();
    handleStartEditing();
  };

  const handleSubmitEdit = () => {
    submitEdit();
    handleStopEditing();
    updateTile(tile.i, { isEditing: false });
    setTouchedTileId(null);
  };

  const handleVideoClick = () => {
    if (videoInfo?.id) {
      setShowModal(true);
    }
  };

  if (tile.static && !isEditing) {
    return (
      <div
        className="flex flex-col items-center h-full w-full justify-center"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleStartEdit();
        }}
      >
        <AddVideoIcon />
        <p className="mt-3 text-sm font-semibold">Add video</p>
      </div>
    );
  }

  if (isMyProfile && isEditing) {
    return (
      <div className="flex flex-col justify-between w-full h-full transition-colors cursor-pointer md:p-5 p-4 text-black bg-red-youtube-active rounded-2xl">
        {tile.isLoading && <LoadingOverlay />}

        <Icon name="video-icon" className="size-8 md:size-11" />
        <div className="flex flex-col space-y-2">
          <Textarea
            style={{
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              resize: 'none',
            }}
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={tempValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleSubmitEdit}
            placeholder="Paste YouTube link here (e.g., https://www.youtube.com/watch?v=...)"
            className="w-full p-0 text-xs md:text-sm border-none focus:outline-none h-fit font-light md:placeholder:text-sm placeholder:text-xs"
          />

          {videoError && (
            <span className="text-red-600 text-xs flex items-center">
              {videoError}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {isMobileScreen ? (
        <div
          style={
            tile.title
              ? {
                  backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent), url(${tile.title})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : { backgroundColor: '#e5e7eb' }
          }
          className={cn(
            'flex flex-col justify-between w-full h-full transition-colors p-4 text-black bg-transparent rounded-2xl relative overflow-hidden',
            {
              'flex-col justify-end gap-5': is1x2,
              'items-end': is2x1,
            }
          )}
          onTouchStart={(e) => {
            if (touchedTileId !== tile.i) {
              e.preventDefault();
              return;
            }
            onTouchStart(e);
          }}
        >
          <Icon
            name="video-icon"
            className={cn('absolute top-2 left-3 size-8 text-white', {
              'md:size-11': !isMobileScreen,
            })}
          />
          <div
            className={cn(
              'absolute bottom-4 left-2 text-white text-xs font-medium hyphens-auto leading-tight line-clamp-3'
            )}
            onTouchStart={(e) => {
              e.stopPropagation();
              handleVideoClick();
            }}
          >
            {videoInfo?.title || tile.content || 'YouTube Video'}
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'flex flex-col justify-between w-full h-full transition-colors p-4 md:p-3 text-black bg-transparent rounded-2xl relative overflow-hidden',
            {
              'flex-col justify-end gap-5': is1x2,
              'items-end': is2x1,
            }
          )}
          onMouseDown={onMouseDown}
        >
          {loadingVideoInfo && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          )}

          <Icon
            name="video-icon"
            className={cn('absolute top-5 left-6 size-11 text-white', {
              'top-2 left-4': is2x1,
            })}
          />
          <img
            src={tile.title}
            alt="Video Thumbnail"
            className={cn('size-44 rounded-md object-cover', {
              'md:w-full md:h-72': is2x2,
            })}
          />

          <div
            className={cn(
              'flex justify-between items-end h-fit relative z-10',
              {
                'flex-col items-center justify-between gap-5': is1x2 || is2x2,
              }
            )}
          >
            <p
              className={cn(
                'text-xs font-medium hyphens-auto leading-tight line-clamp-3 hover:underline cursor-default mt-1 md:text-base'
              )}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleVideoClick();
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                handleVideoClick();
              }}
            >
              {videoInfo?.title || tile.content || 'YouTube Video'}
            </p>

            {!is1x1 && videoInfo && (
              <div
                className={cn('flex space-x-2 ml-2 w-full', {
                  'md:ml-0': is1x2,
                  'justify-end': is2x2 || is2x1,
                })}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleVideoClick();
                }}
              >
                <Button
                  variant="outline"
                  className={cn('text-xs md:text-sm', {
                    'md:text-xs': is1x2,
                    'md:px-6 px-3': is2x1 || is2x2,
                  })}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    tile.content && window.open(tile.content, '_blank');
                  }}
                >
                  <Icon name="video-icon" className="size-4" />
                  Watch on YouTube
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {videoInfo?.id && (
        <YouTubeModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          videoId={videoInfo.id}
          title={videoInfo.title}
          channelTitle={videoInfo.channelTitle}
        />
      )}
    </>
  );
};
